<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TalkviaPixChargesAPI from 'dashboard/api/talkviaPixCharges';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import { validPixAmount } from './pixPayment';

// Cria a cobrança Pix no Asaas da empresa e devolve os dados que preenchem a mensagem. Só
// aparece para quem pode gerar (administrador ou permissão pix_charge_create) com o recurso
// ligado na integração. Nada é enviado ao cliente aqui.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  amount: { type: [String, Number], default: '' },
  description: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['generated']);
const { t } = useI18n();

const canCreate = ref(false);
const isGenerating = ref(false);
const generated = ref(false);
const saoPauloDate = offsetDays => {
  const date = new Date(Date.now() + offsetDays * 86400000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
  }).format(date);
};
const today = saoPauloDate(0);
const dueDate = ref(saoPauloDate(1));

// Mesma chave em nova tentativa com os mesmos dados (o servidor reaproveita a cobrança);
// dados novos geram outra chave.
let attempt = { signature: '', key: '' };
const newKey = () => crypto.randomUUID().replaceAll('-', '');

const canSubmit = computed(
  () =>
    !props.disabled &&
    !isGenerating.value &&
    validPixAmount(props.amount) &&
    !!props.description.trim() &&
    !!dueDate.value
);

const generate = async () => {
  if (!canSubmit.value) return;
  const params = {
    conversation_id: props.conversationId,
    amount: Number(props.amount).toFixed(2),
    description: props.description.trim(),
    due_date: dueDate.value,
  };
  const signature = JSON.stringify(params);
  if (attempt.signature !== signature) attempt = { signature, key: newKey() };

  isGenerating.value = true;
  try {
    const { data } = await TalkviaPixChargesAPI.createCharge({
      ...params,
      idempotency_key: attempt.key,
    });
    generated.value = true;
    emit('generated', data);
    useAlert(t('CONVERSATION.INTERACTIVE.PIX.ASAAS.GENERATED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.INTERACTIVE.PIX.ASAAS.ERROR')
    );
  } finally {
    isGenerating.value = false;
  }
};

onMounted(async () => {
  try {
    const { data } = await TalkviaPixChargesAPI.availability();
    canCreate.value = !!data?.can_create;
  } catch {
    canCreate.value = false;
  }
});
</script>

<template>
  <div
    v-if="canCreate"
    class="flex flex-col gap-3 rounded-xl border border-n-brand/30 bg-n-brand/5 p-3"
    data-test="asaas-pix-charge"
  >
    <div class="flex items-start gap-2">
      <span
        class="i-hugeicons-invoice-03 mt-0.5 size-4 shrink-0 text-n-brand"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ t('CONVERSATION.INTERACTIVE.PIX.ASAAS.TITLE') }}
        </p>
        <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-11">
          {{ t('CONVERSATION.INTERACTIVE.PIX.ASAAS.HELP') }}
        </p>
      </div>
    </div>
    <div class="flex flex-wrap items-end gap-3">
      <DateField
        v-model="dueDate"
        class="min-w-40 flex-1"
        :label="t('CONVERSATION.INTERACTIVE.PIX.ASAAS.DUE_DATE')"
        :min="today"
        :clearable="false"
        size="sm"
        :disabled="disabled || isGenerating"
      />
      <NextButton
        type="button"
        size="sm"
        icon="i-hugeicons-invoice-03"
        :label="
          isGenerating
            ? t('CONVERSATION.INTERACTIVE.PIX.ASAAS.GENERATING')
            : generated
              ? t('CONVERSATION.INTERACTIVE.PIX.ASAAS.REGENERATE')
              : t('CONVERSATION.INTERACTIVE.PIX.ASAAS.GENERATE')
        "
        :is-loading="isGenerating"
        :disabled="!canSubmit"
        data-test="asaas-pix-generate"
        @click="generate"
      />
    </div>
    <p
      v-if="!validPixAmount(amount) || !description.trim()"
      class="mb-0 text-xs text-n-slate-11"
    >
      {{ t('CONVERSATION.INTERACTIVE.PIX.ASAAS.FILL_FIRST') }}
    </p>
  </div>
</template>
